/**
 * serverApi.js
 * Centralized API functions for the Server Dashboard module.
 * All backend calls use the ci4-api base URL (without /api suffix).
 */
import api from '../../utils/api';

export const getBase = () =>
  api.defaults.baseURL ? api.defaults.baseURL.replace(/\/$/, '') : '/api';

// ─── Inventory ────────────────────────────────────────────────────────────────

export const fetchServers = (search = '', page = 0, length = 200) => {
  const params = new URLSearchParams({
    draw: '1',
    start: String(page * length),
    length: String(length),
    'search[value]': search,
  });
  return api.post(`${getBase()}/Server_dashboard/get_servers`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchServerNames = () => {
  const params = new URLSearchParams({ draw: '1', start: '0', length: '500' });
  return api.post(`${getBase()}/Server_dashboard/get_servers`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const addServer = (serverName) => {
  const params = new URLSearchParams({ server_name: serverName });
  return api.post(`${getBase()}/Server_dashboard/add_server`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const deleteServerRecord = (serverId) => {
  const params = new URLSearchParams({ server_id: serverId });
  return api.post(`${getBase()}/Server_dashboard/delete_server_record`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const saveParentField = (id, field, value) => {
  const params = new URLSearchParams({ id, field, value });
  return api.post(`${getBase()}/Server_dashboard/save_server`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const saveChildField = (table, serverId, field, value) => {
  const params = new URLSearchParams({ table, server_id: serverId, field, value });
  return api.post(`${getBase()}/Server_dashboard/save_child_data`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchChildDetails = (serverId) => {
  const params = new URLSearchParams({ server_id: serverId, format: 'json' });
  return api.post(`${getBase()}/Server_dashboard/get_child_details`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const triggerJsonScan = () =>
  api.post(`${getBase()}/Server_dashboard/process_json_sync`);

// ─── Sync Reports ─────────────────────────────────────────────────────────────

export const fetchSyncReports = () =>
  api.post(`${getBase()}/Server_dashboard/get_sync_reports`);

export const fetchLogServersSnapshot = () =>
  api.post(`${getBase()}/Server_dashboard/get_log_servers_snapshot`);

export const deleteSyncReports = (ids) => {
  const params = new URLSearchParams();
  ids.forEach((id) => params.append('ids[]', String(id)));
  return api.post(`${getBase()}/Server_dashboard/delete_sync_reports`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

// ─── Status Dashboard ─────────────────────────────────────────────────────────

export const fetchStatusLogHistory = (serverName = '') => {
  const params = new URLSearchParams({ servername: serverName });
  return api.post(`${getBase()}/get_server_log_history`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchMetricStats = (serverName = '', metric = 'CPULOAD') => {
  const params = new URLSearchParams({ servername: serverName, metric });
  return api.post(`${getBase()}/get_server_log_metric_stats`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const fetchStatusLogs = (serverName = '', start = 0, length = 100, search = '', sortCol = '0', sortDir = 'desc') => {
  const params = new URLSearchParams({
    servername: serverName,
    draw: '1',
    start: String(start),
    length: String(length),
    'order[0][column]': String(sortCol),
    'order[0][dir]': sortDir,
    'search[value]': search,
  });
  return api.post(`${getBase()}/get_server_log`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

// ─── Status Log Actions ───────────────────────────────────────────────────────

export const deleteServerLogs = (serverNames) => {
  const params = new URLSearchParams();
  serverNames.forEach((s) => params.append('server_name[]', s));
  return api.post(`${getBase()}/delete_server`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

export const truncateServerLogs = (serverNames, truncateDate) => {
  const params = new URLSearchParams({ truncate_log: truncateDate });
  serverNames.forEach((s) => params.append('server_name[]', s));
  return api.post(`${getBase()}/delete_server`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
};

// ─── Chatbot ──────────────────────────────────────────────────────────────────

export const askChatbot = (question, history = '[]') => {
  const params = new URLSearchParams({ question, history });
  return api.post(`${getBase()}/Server_dashboard/chatbot_ask`, params, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    timeout: 60000
  });
};

// ─── Customers ────────────────────────────────────────────────────────────────

export const fetchCustomers = async () => {
  const response = await api.get('/documentation/get-customers');
  if (response.data && Array.isArray(response.data.data)) {
    response.data.data = response.data.data.map(c => ({
      ...c,
      cust_key: c.child_id
    }));
  }
  return response;
};

// ─── Offline Cache Helpers ────────────────────────────────────────────────────

const CACHE_KEY = 'offline_server_saves';

export const loadOfflineCache = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
  } catch {
    return {};
  }
};

export const saveOfflineCache = (data) =>
  localStorage.setItem(CACHE_KEY, JSON.stringify(data));

export const clearOfflineCacheItem = (key) => {
  const cache = loadOfflineCache();
  delete cache[key];
  saveOfflineCache(cache);
  return cache;
};
